import { useState, useEffect } from "react";
import type { DashboardSummary } from "@/domain/dashboard/types";
import styles from "./DashboardHeadline.module.css";

interface DashboardHeadlineProps {
  readonly summary: DashboardSummary;
  readonly hasShift: boolean;
}

function LiveClock() {
  const [time, setTime] = useState(() => new Date());

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 10_000);
    return () => clearInterval(interval);
  }, []);

  const h = String(time.getHours()).padStart(2, "0");
  const m = String(time.getMinutes()).padStart(2, "0");

  return (
    <span className={styles.clock}>
      <span className={styles.clockHours}>{h}</span>
      <span className={styles.clockSep}>:</span>
      <span className={styles.clockMinutes}>{m}</span>
    </span>
  );
}

function buildHeadline(summary: DashboardSummary): string {
  const { totalShifts, completed, onSite, needsAttention, onTimePercent } = summary;

  if (totalShifts === 0) {
    return "No shifts today";
  }

  if (needsAttention > 0) {
    return needsAttention === 1
      ? "1 shift needs attention"
      : `${needsAttention} shifts need attention`;
  }

  if (completed === totalShifts) {
    const pct = onTimePercent !== null ? `. ${onTimePercent}% on time` : "";
    return `All shifts complete${pct}`;
  }

  if (completed > 0 && onSite > 0) {
    const onSiteText = onSite === 1 ? "1 on site" : `${onSite} on site`;
    return `${completed} of ${totalShifts} shifts complete. ${onSiteText}`;
  }

  if (completed > 0) {
    return `${completed} of ${totalShifts} shifts complete`;
  }

  return totalShifts === 1
    ? "All 1 shift on track today"
    : `All ${totalShifts} shifts on track today`;
}

export function DashboardHeadline({ summary, hasShift }: DashboardHeadlineProps) {
  if (summary.totalShifts === 0 && !hasShift) {
    return null;
  }
  return (
    <div className={styles.wrapper}>
      <div className={styles.headline}>{buildHeadline(summary)}</div>
      <LiveClock />
    </div>
  );
}
